import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  getUserSessions,
  revokeAllSessionsByUserId,
  revokeSessionBySid,
} from "../services/sessionService";
import parseUserAgent from "../utils/parseUserAgent";
import ConfirmModal from "./modals/ConfirmModal";

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("nl-NL");
}

export default function SessionList({ userId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [revokingSid, setRevokingSid] = useState("");
  const [revokingAll, setRevokingAll] = useState(false);
  const [confirmRevokeSid, setConfirmRevokeSid] = useState("");
  const [confirmRevokeAllOpen, setConfirmRevokeAllOpen] = useState(false);

  useEffect(() => {
    let active = true;

    async function load() {
      setLoading(true);
      setError("");
      try {
        const data = await getUserSessions(userId);
        if (!active) return;
        setItems(Array.isArray(data?.sessions) ? data.sessions : []);
      } catch (err) {
        if (!active) return;
        setError(err?.response?.data?.message || "Sessies ophalen mislukt");
      } finally {
        if (active) setLoading(false);
      }
    }

    if (userId) {
      load();
    } else {
      setItems([]);
      setLoading(false);
    }

    return () => {
      active = false;
    };
  }, [userId]);

  const sortedItems = useMemo(
    () =>
      [...items].sort((a, b) => {
        const tA = new Date(a?.lastSeenAt || 0).getTime();
        const tB = new Date(b?.lastSeenAt || 0).getTime();
        return tB - tA;
      }),
    [items]
  );

  async function handleRevoke(sid) {
    const value = String(sid || "").trim();
    if (!value || revokingSid) return;
    setRevokingSid(value);
    setError("");
    try {
      await revokeSessionBySid(value);
      toast.success("Sessie beëindigd");
      setItems((prev) => prev.filter((session) => String(session.sid) !== value));
    } catch (err) {
      setError(err?.response?.data?.message || "Sessie intrekken mislukt");
    } finally {
      setRevokingSid("");
      setConfirmRevokeSid("");
    }
  }

  async function handleRevokeAll() {
    const id = String(userId || "").trim();
    if (!id || revokingAll) return;
    setRevokingAll(true);
    setError("");
    try {
      await revokeAllSessionsByUserId(id);
      setItems([]);
      toast.success("Alle sessies beëindigd");
    } catch (err) {
      setError(err?.response?.data?.message || "Alle sessies intrekken mislukt");
    } finally {
      setRevokingAll(false);
      setConfirmRevokeAllOpen(false);
    }
  }

  if (loading) {
    return <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600">Sessies laden...</div>;
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-semibold text-slate-800">Actieve sessies</div>
        <button
          type="button"
          onClick={() => setConfirmRevokeAllOpen(true)}
          disabled={sortedItems.length === 0 || revokingAll || Boolean(revokingSid)}
          className="rounded border border-rose-300 bg-white px-3 py-2 text-sm font-medium text-rose-700 disabled:opacity-50"
        >
          {revokingAll ? "Bezig..." : "Log alle sessies uit"}
        </button>
      </div>
      {error ? (
        <div className="mb-3 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>
      ) : null}
      {sortedItems.length === 0 ? (
        <div className="text-sm text-slate-500">Geen actieve sessies gevonden.</div>
      ) : (
        <div className="space-y-2">
          {sortedItems.map((session) => {
            const sid = String(session.sid || "");
            return (
              <div key={sid} className="flex flex-col gap-2 rounded border border-slate-200 bg-slate-50 p-3 md:flex-row md:items-center md:justify-between">
                <div className="text-sm text-slate-700">
                  <div className="font-medium text-slate-900">
                    {session.deviceName || session.device || parseUserAgent(session.userAgent)}
                    {session.isCurrentSession ? (
                      <span className="ml-2 inline-flex rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
                        Huidige sessie
                      </span>
                    ) : null}
                  </div>
                  <div>IP: {session.ip || "-"}</div>
                  <div>Laatst actief: {formatDate(session.lastSeenAt)}</div>
                  <div>Ingelogd op: {formatDate(session.createdAt)}</div>
                </div>
                <button
                  type="button"
                  onClick={() => setConfirmRevokeSid(sid)}
                  disabled={!sid || revokingSid === sid || revokingAll}
                  className="rounded border border-rose-300 bg-white px-3 py-2 text-sm font-medium text-rose-700 disabled:opacity-50"
                >
                  {revokingSid === sid ? "Bezig..." : "Log uit"}
                </button>
              </div>
            );
          })}
        </div>
      )}
      <ConfirmModal
        open={Boolean(confirmRevokeSid)}
        title="Sessie beëindigen"
        message="Weet je zeker dat je deze sessie wilt beëindigen?"
        confirmLabel="Log uit"
        loading={Boolean(confirmRevokeSid) && revokingSid === confirmRevokeSid}
        onClose={() => {
          if (revokingSid) return;
          setConfirmRevokeSid("");
        }}
        onConfirm={() => handleRevoke(confirmRevokeSid)}
      />
      <ConfirmModal
        open={confirmRevokeAllOpen}
        title="Alle sessies beëindigen"
        message="Alle apparaten worden uitgelogd. Doorgaan?"
        confirmLabel="Log alle sessies uit"
        loading={revokingAll}
        onClose={() => {
          if (revokingAll) return;
          setConfirmRevokeAllOpen(false);
        }}
        onConfirm={handleRevokeAll}
      />
    </div>
  );
}
